/* ---------------------------------------------------------------------------
 * MD Fashionwear — find the preorder twin of an in-stock product
 *
 * THE SHAPE OF THE CATALOGUE
 * Many pieces exist TWICE: once as local Lebanon stock (tagged ONLINE) and once
 * as an overseas preorder (tagged preorder). That is what lets us offer a size
 * the local warehouse has run out of.
 *
 * MEASURED 30 Jul 2026 (1,109 products, 1,037 titles)
 *      66 titles have BOTH an ONLINE and a preorder product  <- feature applies
 *     273 are ONLINE only   -> a sold-out size cannot be preordered, no source
 *     698 are preorder only -> every size already is a preorder
 *
 * SPEED — DO NOT REINTRODUCE A CATALOGUE CRAWL ON THE CRITICAL PATH
 * A previous version walked /products.json 5 pages deep (~5 MB) to build a
 * title index. Measured cold, the amber preorder sizes had still not appeared
 * after 10 seconds. The data was right; it arrived far too late to be useful.
 *
 * HOW IT WORKS NOW — one small verified request
 *   sera-mini-dress-1  -> sera-mini-dress      (strip the numeric suffix)
 *   parisian-dress     -> parisian-dress-copy  (add -copy)
 *   irie-maxi-dress    -> irie-maxi-dress-2    (add -2)
 * Candidates are fetched and ACCEPTED ONLY if the normalised title matches AND
 * the preorder tag is present AND it is a different product. That verification
 * is what makes handle guessing safe. 64 of the 66 pairs resolve this way; the
 * 2 that share no handle relationship are in OVERRIDES.
 *
 * WHY THERE IS NO REPAINT HOOK HERE — READ BEFORE ADDING ONE BACK
 * An earlier version of this file called MDWishlistPage.render() as soon as a
 * twin was found, to force the row to redraw. That made things worse, not
 * better: render() clears the whole grid (grid.innerHTML = ''), so any twinFor
 * promise still in flight resolved against a row element that had already been
 * thrown away and painted into a detached node nobody could see. The reported
 * symptom was sizes stuck disabled and other colours stuck unclickable until
 * something forced another render.
 *
 * Repainting is md-wishlist-page.js's job, and it already does it: build()
 * awaits twinFor() and calls paint() on ITS OWN row. This file must only
 * resolve data and must never touch the DOM.
 *
 * PRICE UNITS — a real trap. /products.json returns decimal strings ("55.00");
 * /products/{handle}.js returns integer cents (5500). This file sources from
 * the latter and converts back. Change that and every twin price becomes 100x.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var CACHE_KEY = 'md_twin_v3';
  var TTL = 30 * 60 * 1000;
  var mem = {};          // handle -> slim product | null
  var inflight = {};     // handle -> Promise, so two rows never fetch twice
  var crawl = null;      // background fallback, built at most once

  /* The two pairs the handle rule cannot reach. Same title, unrelated handles. */
  var OVERRIDES = {
    'ruffleminidress': 'white-off-shoulder-ruffle-mini-dress',
    'lumiereminidress': 'lady-solid-mini-dress'
  };

  function norm(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function hasTag(p, tag) {
    var tags = (p && p.tags) || [];
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toLowerCase().replace(/[^a-z]/g, '') === tag) return true;
    }
    return false;
  }

  function money(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return null;
    return (n / 100).toFixed(2);
  }

  function slim(p) {
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      preorder: hasTag(p, 'preorder'),
      online: hasTag(p, 'online'),
      options: (p.options || []).map(function (o, i) {
        if (typeof o === 'string') return { name: o, position: i + 1 };
        return { name: o.name, position: o.position || (i + 1) };
      }),
      variants: (p.variants || []).map(function (v) {
        return {
          id: v.id,
          option1: v.option1,
          option2: v.option2,
          option3: v.option3,
          available: v.available,
          price: money(v.price),
          price_cents: v.price,
          featured_image: (v.featured_image && v.featured_image.src)
            ? { src: v.featured_image.src } : null
        };
      })
    };
  }

  /* ---- the small per-handle cache -------------------------------------- */

  function readCache() {
    try {
      var o = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (!o || Date.now() - o.t > TTL) return {};
      return o.m || {};
    } catch (e) { return {}; }
  }

  function writeCache() {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), m: mem }));
    } catch (e) { /* quota only costs a refetch */ }
  }

  mem = readCache();

  /* ---- candidate handles ----------------------------------------------- */

  function candidates(handle) {
    var h = String(handle || '');
    if (!h) return [];
    var base = h.replace(/-(\d+|copy)$/, '');
    var list = [base, base + '-copy', base + '-2', base + '-3', h + '-copy'];
    var seen = {}, out = [];
    list.forEach(function (c) {
      if (!c || c === h || seen[c]) return;
      seen[c] = 1; out.push(c);
    });
    return out;
  }

  function slugOf(title) {
    return String(title || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function fetchProduct(handle) {
    if (Object.prototype.hasOwnProperty.call(mem, handle)) {
      return Promise.resolve(mem[handle]);
    }
    if (inflight[handle]) return inflight[handle];

    inflight[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        /* Only a real 404 means "this handle does not exist" and is worth
           remembering. Any other failure may be a throttle or a blip, and
           caching null for those would disable the feature for 30 minutes. */
        if (r.status === 404) return { __missing: true };
        if (!r.ok) return null;
        return r.json();
      })
      .then(function (p) {
        delete inflight[handle];
        if (p && p.__missing) { mem[handle] = null; writeCache(); return null; }
        if (!p || !p.variants) return null;      // not cached: might be transient
        var s = slim(p);
        mem[handle] = s;
        writeCache();
        return s;
      })
      .catch(function () {
        delete inflight[handle];
        return null;
      });

    return inflight[handle];
  }

  function accepts(cand, product) {
    if (!cand) return false;
    if (String(cand.id) === String(product.id)) return false;
    if (!cand.preorder) return false;
    if (norm(cand.title) !== norm(product.title)) return false;
    return true;
  }

  /* ---- background fallback (never on the critical path) ----------------- */

  function backgroundCrawl() {
    if (crawl) return crawl;
    crawl = new Promise(function (resolve) {
      var map = {};
      var page = 1;
      function step() {
        if (page > 5) return resolve(map);
        fetch('/products.json?limit=250&page=' + page)
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (j) {
            var list = (j && j.products) || [];
            if (!list.length) return resolve(map);
            list.forEach(function (p) {
              if (!hasTag(p, 'preorder')) return;
              var k = norm(p.title);
              if (!map[k]) map[k] = p.handle;
            });
            page++;
            step();
          })
          .catch(function () { resolve(map); });
      }
      step();
    });
    return crawl;
  }

  function idle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 3000 });
    else setTimeout(fn, 1200);
  }

  /* ---- public ---------------------------------------------------------- */

  function twinFor(product) {
    if (!product || !product.title) return Promise.resolve(null);

    if (hasTag(product, 'preorder') || product.preorder === true) {
      return Promise.resolve(null);
    }

    var key = norm(product.title);
    var order = [];
    if (OVERRIDES[key]) order.push(OVERRIDES[key]);
    order = order.concat(candidates(product.handle || slugOf(product.title)));

    var i = 0;
    function tryNext() {
      if (i >= order.length) return lastResort();
      var h = order[i++];
      return fetchProduct(h).then(function (cand) {
        return accepts(cand, product) ? cand : tryNext();
      });
    }

    function lastResort() {
      return new Promise(function (resolve) {
        idle(function () {
          backgroundCrawl().then(function (map) {
            var h = map[key];
            if (!h || h === product.handle) return resolve(null);
            fetchProduct(h).then(function (cand) {
              resolve(accepts(cand, product) ? cand : null);
            });
          });
        });
      });
    }

    return tryNext();
  }

  function sizePos(p) {
    var opts = (p && p.options) || [];
    for (var i = 0; i < opts.length; i++) {
      var n = String(opts[i].name || '').toLowerCase();
      if (n.indexOf('size') > -1) return opts[i].position || (i + 1);
    }
    return 0;
  }

  function colorPos(p) {
    var opts = (p && p.options) || [];
    for (var i = 0; i < opts.length; i++) {
      var n = String(opts[i].name || '').toLowerCase();
      if (n.indexOf('color') > -1 || n.indexOf('colour') > -1) return opts[i].position || (i + 1);
    }
    return 0;
  }

  function findVariant(twin, colorName, sizeName) {
    if (!twin) return null;
    var sp = sizePos(twin);
    var cp = colorPos(twin);
    var matches = (twin.variants || []).filter(function (v) {
      if (!v.available) return false;
      if (sp && sizeName && v['option' + sp] !== sizeName) return false;
      if (cp && colorName && v['option' + cp] !== colorName) return false;
      return true;
    });
    return matches[0] || null;
  }

  function build() { return Promise.resolve({}); }

  window.MDTwin = {
    build: build,
    twinFor: twinFor,
    findVariant: findVariant,
    sizePos: sizePos,
    colorPos: colorPos,
    _candidates: candidates
  };
})();
